/**
 * Family Service
 *
 * Provides a clean abstraction layer between React components and Firebase.
 * All family CRUD operations go through this service.
 *
 * Benefits:
 * - Components don't need to know about Firebase internals
 * - Easy to mock for testing
 * - Centralized error handling
 * - Single place to add caching, retries, etc.
 */

import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { functions, storage, db, auth } from '../firebase';

/**
 * @typedef {Object} Family
 * @property {string} id - Family document ID
 * @property {string} surname - Family surname
 * @property {string} [street] - Street address
 * @property {string} [city] - City
 * @property {string} [state] - State (2-letter code)
 * @property {string} [zipcode] - Zipcode
 * @property {string} [phone] - Home phone
 * @property {string} [anniversaryMonth] - Anniversary month (1-12)
 * @property {string} [anniversaryDay] - Anniversary day (1-31)
 * @property {string} [anniversaryYear] - Anniversary year
 * @property {string} [photo] - Photo storage path
 * @property {number} [lat] - Latitude from geocoding
 * @property {number} [lng] - Longitude from geocoding
 * @property {Object} [imageUpdatedAt] - Timestamp for cache busting
 */

/**
 * @typedef {Object} CreateFamilyInput
 * @property {string} surname - Family surname (required)
 * @property {string} [street] - Street address
 * @property {string} [city] - City
 * @property {string} [state] - State (2-letter code)
 * @property {string} [zipcode] - Zipcode
 * @property {string} [phone] - Home phone
 * @property {string} [anniversaryMonth] - Anniversary month (1-12)
 * @property {string} [anniversaryDay] - Anniversary day (1-31)
 * @property {string} [anniversaryYear] - Anniversary year
 * @property {File} [photoFile] - Photo file to upload
 */

/**
 * @typedef {Object} UpdateFamilyInput
 * @property {string} familyId - Family ID (required)
 * @property {string} [surname] - Updated surname
 * @property {string} [street] - Updated street
 * @property {string} [city] - Updated city
 * @property {string} [state] - Updated state
 * @property {string} [zipcode] - Updated zipcode
 * @property {string} [phone] - Updated phone
 * @property {string} [anniversaryMonth] - Updated anniversary month
 * @property {string} [anniversaryDay] - Updated anniversary day
 * @property {string} [anniversaryYear] - Updated anniversary year
 * @property {File|null} [photoFile] - New photo file, or null to remove
 */

/**
 * @typedef {Object} FamilyServiceResult
 * @property {boolean} success - Whether the operation succeeded
 * @property {string} [familyId] - Family ID (for create operations)
 * @property {string} [message] - Success/error message
 * @property {string} [photoPath] - Photo storage path
 * @property {Object} [imageUpdatedAt] - Timestamp for cache busting
 * @property {string} [error] - Error message if failed
 */

// Callable function references
const addFamilyV2 = httpsCallable(functions, 'addFamilyV2');
const updateFamilyV2 = httpsCallable(functions, 'updateFamilyV2');
const deleteFamilyFn = httpsCallable(functions, 'deleteFamily');

/**
 * Get the current user's organization ID from claims
 * @returns {Promise<string>} The organization ID
 * @throws {Error} If user is not authenticated or missing orgId claim
 */
async function getOrgId() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idTokenResult = await user.getIdTokenResult();
  const orgId = idTokenResult.claims.orgId;

  if (!orgId) {
    throw new Error('Organization ID not found in user claims. Please re-login.');
  }

  return orgId;
}

/**
 * Upload a photo to temporary storage
 * @param {File} file - The file to upload
 * @param {string} orgId - Organization ID
 * @param {function(number): void} [onProgress] - Progress callback (0-100)
 * @returns {Promise<string>} The storage path
 */
async function uploadTempPhoto(file, orgId, onProgress) {
  const fileExtension = file.name.split('.').pop();
  const fileName = `${orgId}/photos/${uuidv4()}.${fileExtension}`;

  const storageRef = ref(storage, fileName);
  const uploadTask = uploadBytesResumable(storageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (onProgress) {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        }
      },
      (error) => {
        reject(new Error(`Photo upload failed: ${error.message}`));
      },
      () => {
        resolve(fileName);
      }
    );
  });
}

/**
 * Get the download URL for a family photo
 * @param {string} photoPath - The storage path
 * @param {Object} [imageUpdatedAt] - Timestamp for cache busting
 * @returns {Promise<string|null>} The download URL or null
 */
async function getFamilyPhotoUrl(photoPath, imageUpdatedAt) {
  if (!photoPath) {
    return null;
  }

  try {
    const photoRef = ref(storage, photoPath);
    let url = await getDownloadURL(photoRef);

    // Add cache-busting parameter if we have a timestamp
    if (imageUpdatedAt) {
      const timestamp = imageUpdatedAt.seconds || imageUpdatedAt._seconds || Date.now() / 1000;
      url = `${url}&t=${timestamp}`;
    }

    return url;
  } catch (error) {
    console.error('Error getting photo URL:', error);
    return null;
  }
}

/**
 * Create a new family
 * @param {CreateFamilyInput} input - Family data
 * @param {Object} [options] - Options
 * @param {function(number): void} [options.onUploadProgress] - Upload progress callback
 * @returns {Promise<FamilyServiceResult>} Result with familyId
 */
async function createFamily(input, options = {}) {
  const { onUploadProgress } = options;

  try {
    const orgId = await getOrgId();
    let photoPath = null;

    // Upload photo if provided
    if (input.photoFile) {
      photoPath = await uploadTempPhoto(input.photoFile, orgId, onUploadProgress);
    }

    // Prepare data for Cloud Function
    const familyData = {
      surname: input.surname?.trim() || '',
      street: input.street?.trim() || null,
      city: input.city?.trim() || null,
      state: input.state?.trim().toUpperCase() || null,
      zipcode: input.zipcode?.trim() || null,
      phone: input.phone?.trim() || null,
      anniversaryMonth: input.anniversaryMonth ? String(input.anniversaryMonth) : null,
      anniversaryDay: input.anniversaryDay ? String(input.anniversaryDay) : null,
      anniversaryYear: input.anniversaryYear ? String(input.anniversaryYear) : null,
      photo: photoPath,
    };

    const result = await addFamilyV2(familyData);

    if (result.data?.success) {
      return {
        success: true,
        familyId: result.data.familyId,
        message: result.data.message || 'Family created successfully',
        photoPath: result.data.photoPath,
        imageUpdatedAt: result.data.imageUpdatedAt,
      };
    }

    throw new Error(result.data?.message || 'Failed to create family');
  } catch (error) {
    console.error('Error creating family:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Update an existing family
 * @param {UpdateFamilyInput} input - Updated family data
 * @param {Object} [options] - Options
 * @param {function(number): void} [options.onUploadProgress] - Upload progress callback
 * @returns {Promise<FamilyServiceResult>} Result
 */
async function updateFamily(input, options = {}) {
  const { onUploadProgress } = options;

  try {
    const orgId = await getOrgId();

    // Handle photo updates
    let photoInstruction = undefined; // undefined = no change
    if (input.photoFile === null) {
      // Explicit null = remove photo
      photoInstruction = null;
    } else if (input.photoFile instanceof File) {
      // New file = upload and use temp path
      photoInstruction = await uploadTempPhoto(input.photoFile, orgId, onUploadProgress);
    }

    // Prepare update payload (only include fields that are being updated)
    const updateData = {
      familyId: input.familyId,
    };

    if (input.surname !== undefined) updateData.surname = input.surname?.trim();
    if (input.street !== undefined) updateData.street = input.street?.trim() || null;
    if (input.city !== undefined) updateData.city = input.city?.trim() || null;
    if (input.state !== undefined) updateData.state = input.state?.trim().toUpperCase() || null;
    if (input.zipcode !== undefined) updateData.zipcode = input.zipcode?.trim() || null;
    if (input.phone !== undefined) updateData.phone = input.phone?.trim() || null;
    if (input.anniversaryMonth !== undefined) {
      updateData.anniversaryMonth = input.anniversaryMonth ? String(input.anniversaryMonth) : null;
    }
    if (input.anniversaryDay !== undefined) {
      updateData.anniversaryDay = input.anniversaryDay ? String(input.anniversaryDay) : null;
    }
    if (input.anniversaryYear !== undefined) {
      updateData.anniversaryYear = input.anniversaryYear ? String(input.anniversaryYear) : null;
    }
    if (photoInstruction !== undefined) {
      updateData.photo = photoInstruction;
    }

    const result = await updateFamilyV2(updateData);

    if (result.data?.success) {
      return {
        success: true,
        message: result.data.message || 'Family updated successfully',
        photoPath: result.data.photoPath,
        imageUpdatedAt: result.data.imageUpdatedAt,
      };
    }

    throw new Error(result.data?.message || 'Failed to update family');
  } catch (error) {
    console.error('Error updating family:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Delete a family
 * @param {string} familyId - Family ID to delete
 * @returns {Promise<FamilyServiceResult>} Result
 */
async function deleteFamily(familyId) {
  try {
    const result = await deleteFamilyFn({ familyId });

    if (result.data?.success) {
      return {
        success: true,
        message: result.data.message || 'Family deleted successfully',
      };
    }

    throw new Error(result.data?.message || 'Failed to delete family');
  } catch (error) {
    console.error('Error deleting family:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Get a single family by ID
 * @param {string} familyId - Family ID
 * @returns {Promise<Family|null>} The family or null if not found
 */
async function getFamily(familyId) {
  try {
    const orgId = await getOrgId();
    const familyDoc = await getDoc(doc(db, 'family', familyId));

    if (!familyDoc.exists()) {
      return null;
    }

    const data = familyDoc.data();

    // Verify org isolation
    if (data.orgId !== orgId) {
      console.warn('Attempted to access family from different org');
      return null;
    }

    return {
      ...data,
      id: familyDoc.id,  // Put after spread so document's id:null doesn't overwrite
    };
  } catch (error) {
    console.error('Error fetching family:', error);
    throw error;
  }
}

/**
 * Get all families for the current user's organization
 * @param {Object} [options] - Query options
 * @param {string} [options.orderByField='surname'] - Field to order by
 * @returns {Promise<Family[]>} Array of families
 */
async function getFamilies(options = {}) {
  const { orderByField = 'surname' } = options;

  try {
    const orgId = await getOrgId();

    const familiesQuery = query(
      collection(db, 'family'),
      where('orgId', '==', orgId),
      orderBy(orderByField)
    );

    const snapshot = await getDocs(familiesQuery);
    const families = [];

    snapshot.forEach((doc) => {
      families.push({
        ...doc.data(),
        id: doc.id,  // Put after spread so document's id:null doesn't overwrite
      });
    });

    return families;
  } catch (error) {
    console.error('Error fetching families:', error);
    throw error;
  }
}

/**
 * Extract user-friendly error message from various error types
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
function extractErrorMessage(error) {
  // Firebase Functions error
  if (error.code && error.message) {
    return error.message;
  }

  // Standard Error
  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}

// Export the service as a single object
const familyService = {
  createFamily,
  updateFamily,
  deleteFamily,
  getFamily,
  getFamilies,
  getFamilyPhotoUrl,
  getOrgId,
};

export default familyService;

// Also export individual functions for tree-shaking
export {
  createFamily,
  updateFamily,
  deleteFamily,
  getFamily,
  getFamilies,
  getFamilyPhotoUrl,
  getOrgId,
  extractErrorMessage,
};
